import { ROLES } from "./const.js";
import { isCoreInstance, type InstanceConfig } from "./instance.js";

/** Nur Server und Client mit ihrem bereits geparsten Instanzprofil rufen diese Capability auf. */
type InstanceProfile = Pick<InstanceConfig, "productName"> | undefined;
type StoredRoleScope = string | null | undefined;

/**
 * GF-Capability des Firmen-Hubs. Core-Administration wird ausschließlich aus
 * public.core_person_settings bestimmt (auth middleware), nie aus einer Rolle.
 */
export function isGfRole(
  roleName: string | null | undefined,
  instance?: InstanceProfile,
  roleScope?: StoredRoleScope,
): boolean {
  if (isCoreInstance(instance ?? { productName: "COMBSOL/OS" })) return false;
  return roleName === ROLES.GF;
}

/** Firmen-Admin-Capability; Core setzt den DB-frischen Personenflag im AuthContext. */
export function isAdminRole(
  roleName: string | null | undefined,
  instance?: InstanceProfile,
  roleScope?: StoredRoleScope,
): boolean {
  if (isCoreInstance(instance ?? { productName: "COMBSOL/OS" })) return false;
  return isGfRole(roleName, instance, roleScope) || roleName === ROLES.Admin;
}

/** MFA-Rollenpflicht im Firmen-Hub; Core-Administratoren werden separat zentral geprüft. */
export function requiresMfaForRole(
  roleName: string | null | undefined,
  instance?: InstanceProfile,
  roleScope?: StoredRoleScope,
): boolean {
  if (isCoreInstance(instance ?? { productName: "COMBSOL/OS" })) return false;
  if (roleName === ROLES.GF || roleName === ROLES.Admin) return true;
  return false;
}

/** Rolle „Verwaltung“: nur Kontaktdaten interner Mitarbeiter im Firmenprofil. */
export function isVerwaltungRole(roleName: string | null | undefined, instance?: InstanceProfile): boolean {
  return !isCoreInstance(instance ?? { productName: "COMBSOL/OS" }) && roleName === ROLES.VERWALTUNG;
}

/** Wer Kontaktdaten pflegen darf: Hub-Führung oder Verwaltung nur im Firmenprofil. */
export function canManageContactData(
  roleName: string | null | undefined,
  instance?: InstanceProfile,
  roleScope?: StoredRoleScope,
): boolean {
  return isGfRole(roleName, instance, roleScope) || isVerwaltungRole(roleName, instance);
}

/**
 * Eng begrenzte Consumer-Kompatibilität: ein bereits migrierter, immutable
 * legacy_data_owner kann bis zum Consumer-Cutover für Money als GF transportiert
 * werden. Ein Core-Administratorflag oder eine historische Core-Rolle genügt
 * ausdrücklich nicht. Firmenpfade bleiben unverändert.
 */
export function ssoTransportRole(
  roleName: string,
  appKey: string,
  instance: Pick<InstanceConfig, "productName">,
  roleScope?: StoredRoleScope,
  legacyDataOwner = false,
): {
  roleName: string;
  originalRoleKey?: string;
} {
  if (appKey === "money" && isCoreInstance(instance) && legacyDataOwner) {
    return { roleName: ROLES.GF, originalRoleKey: "legacy_data_owner" };
  }
  return { roleName };
}
